import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { Input } from '@/components/ui/input';
import { monthLabel } from '../list/list-query';
import { monthsFrom, resolvedType, type NewReportForm } from './new-report-form';
import type { NoteType, PurposeChoice, TripScope } from './types';

type Props = {
  readonly form: NewReportForm;
  readonly purposes: readonly PurposeChoice[];
  readonly firstMonth: string;
  /** A note sent back keeps its month: shown, not offered. */
  readonly monthFixed?: boolean;
  readonly onChange: (patch: Partial<NewReportForm>) => void;
};

const SCOPES: readonly TripScope[] = ['italy', 'abroad'];

function Labelled({
  id,
  label,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
      </label>
      {children}
    </div>
  );
}

/** The purpose, then the type when the purpose allows more than one, then the month (spec R-a). */
export function PurposeFields({ form, purposes, firstMonth, monthFixed, onChange }: Props) {
  const { t, i18n } = useTranslation('expenses');
  const uid = useId();
  const purpose = purposes.find((p) => p.id === form.purposeId);
  return (
    <>
      <Labelled id={`${uid}-purpose`} label={t('reports.new.purpose')}>
        <ChoiceSelect
          itemClassName="min-h-11"
          id={`${uid}-purpose`}
          value={form.purposeId}
          onChange={(purposeId) => onChange({ purposeId, noteType: '' })}
          choices={[
            { value: '', label: t('reports.new.purposePlaceholder') },
            ...purposes.map((p) => ({ value: p.id, label: `${p.code} · ${p.label}` })),
          ]}
        />
      </Labelled>
      {purpose && purpose.noteTypes.length > 1 ? (
        <Labelled id={`${uid}-type`} label={t('reports.new.noteType')}>
          <ChoiceSelect
            itemClassName="min-h-11"
            id={`${uid}-type`}
            value={form.noteType}
            onChange={(noteType) => onChange({ noteType: noteType as NoteType | '' })}
            choices={[
              { value: '', label: t('reports.new.noteTypePlaceholder') },
              ...purpose.noteTypes.map((type) => ({
                value: type,
                label: t(`reports.noteType.${type}`),
              })),
            ]}
          />
        </Labelled>
      ) : null}
      {monthFixed ? (
        <Labelled id={`${uid}-month`} label={t('reports.new.month')}>
          <Input
            id={`${uid}-month`}
            readOnly
            value={monthLabel(form.month, i18n.resolvedLanguage)}
            aria-describedby={`${uid}-month-fixed`}
          />
          <small id={`${uid}-month-fixed`} className="text-muted-foreground text-sm">
            {t('reports.edit.monthFixed')}
          </small>
        </Labelled>
      ) : (
        <Labelled id={`${uid}-month`} label={t('reports.new.month')}>
          <ChoiceSelect
            itemClassName="min-h-11"
            id={`${uid}-month`}
            value={form.month}
            onChange={(month) => onChange({ month })}
            choices={monthsFrom(firstMonth).map((m) => ({
              value: m,
              label: monthLabel(m, i18n.resolvedLanguage),
            }))}
          />
        </Labelled>
      )}
    </>
  );
}

/** A Trasferta's start, end and Italia/Estero. */
export function TripFields({ form, purposes, onChange }: Omit<Props, 'firstMonth'>) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  const type = resolvedType(
    purposes.find((p) => p.id === form.purposeId),
    form.noteType,
  );
  if (type !== 'travel') return null;
  const reversed = form.tripStart !== '' && form.tripEnd !== '' && form.tripEnd < form.tripStart;
  return (
    <fieldset className="border-border flex flex-col gap-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">{t('reports.new.trip')}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <Labelled id={`${uid}-start`} label={t('reports.new.tripStart')}>
          <Input
            id={`${uid}-start`}
            type="date"
            value={form.tripStart}
            onChange={(event) => onChange({ tripStart: event.target.value })}
          />
        </Labelled>
        <Labelled id={`${uid}-end`} label={t('reports.new.tripEnd')}>
          <Input
            id={`${uid}-end`}
            type="date"
            value={form.tripEnd}
            aria-invalid={reversed}
            onChange={(event) => onChange({ tripEnd: event.target.value })}
          />
        </Labelled>
      </div>
      {reversed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('reports.new.problems.TRIP_DATES_INVALID')}
        </p>
      ) : null}
      <Labelled id={`${uid}-scope`} label={t('reports.new.tripScope')}>
        <ChoiceSelect
          itemClassName="min-h-11"
          id={`${uid}-scope`}
          value={form.tripScope}
          onChange={(tripScope) => onChange({ tripScope: tripScope as TripScope | '' })}
          choices={[
            { value: '', label: t('reports.new.tripScopePlaceholder') },
            ...SCOPES.map((scope) => ({ value: scope, label: t(`reports.tripScope.${scope}`) })),
          ]}
        />
      </Labelled>
    </fieldset>
  );
}
